/**
 * /api/v2 TradingAgents endpoints — same base rules as AuthContext/api.ts:
 * VITE_API_BASE_URL = backend origin (no trailing path); empty = same-origin + Vite proxy.
 */
const API_V2_BASE = import.meta.env.VITE_API_BASE_URL
  ? `${String(import.meta.env.VITE_API_BASE_URL).replace(/\/$/, "")}/api/v2`
  : "/api/v2";

function authHeaders(jsonBody = false): HeadersInit {
  const token = localStorage.getItem("access_token");
  const h: Record<string, string> = {};
  if (jsonBody) h["Content-Type"] = "application/json";
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

export function getTradingAgentsV2Base(): string {
  return API_V2_BASE;
}

// ── Analysis ──

export async function v2StartAnalyze(body: {
  ticker: string;
  trade_date: string;
  llm_provider?: string;
  strategy_id?: number;
}): Promise<{ status: string; run_id: string }> {
  const res = await fetch(`${API_V2_BASE}/analyze`, {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || res.statusText);
  }
  return data as { status: string; run_id: string };
}

export async function v2ListRuns(params?: {
  ticker?: string;
  limit?: number;
}): Promise<{ runs: RunRow[]; count: number }> {
  const q = new URLSearchParams();
  if (params?.ticker) q.set("ticker", params.ticker);
  if (params?.limit != null) q.set("limit", String(params.limit));
  const res = await fetch(`${API_V2_BASE}/runs?${q}`, {
    headers: authHeaders(false),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || res.statusText);
  }
  return data as { runs: RunRow[]; count: number };
}

export interface RunRow {
  run_id: string;
  ticker: string;
  trade_date: string;
  status: string;
  llm_provider?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

// ── Phase 6: source credibility, contradictions, agent timeline ──

export interface SourceCredibility {
  source_type: string;
  title: string;
  url?: string;
  platform?: string | null;
  authority: number;
  recency: number;
  corroboration: number;
  bias: number;
  credibility: number;
  label: string;
}

export interface Contradiction {
  topic: string;
  side_a: string;
  side_b: string;
  severity: "high" | "medium" | "low" | string;
}

export interface AgentLogEntry {
  agent: string;
  started_at: string;
  ended_at?: string | null;
  duration_ms?: number | null;
  input_summary?: string;
  output_summary?: string;
}

/** Phase 6 fields surfaced inside the run's `state` payload by the backend. */
export interface RunState extends Record<string, unknown> {
  source_credibility?: SourceCredibility[];
  contradictions?: Contradiction[];
  confidence_band?: string;
  agent_log?: AgentLogEntry[];
}

export interface RunDetail {
  run_id: string;
  ticker: string;
  trade_date: string;
  llm_provider?: string | null;
  status: string;
  error_message?: string | null;
  state?: RunState;
  extras?: Record<string, unknown>;
  created_at?: string | null;
  updated_at?: string | null;
}

export async function v2GetRun(runId: string): Promise<RunDetail> {
  const res = await fetch(`${API_V2_BASE}/runs/${encodeURIComponent(runId)}`, {
    headers: authHeaders(false),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || res.statusText);
  }
  return data as RunDetail;
}

export async function v2DownloadReport(
  runId: string,
  format: "html" | "pdf"
): Promise<Blob> {
  const res = await fetch(
    `${API_V2_BASE}/reports/${encodeURIComponent(runId)}`,
    {
      method: "POST",
      headers: authHeaders(true),
      body: JSON.stringify({ format }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || res.statusText);
  }
  return res.blob();
}

// ── SSE Streaming ──

export interface AgentSSEEvent {
  run_id: string;
  agent: string;
  event_type: "started" | "progress" | "completed" | "error";
  message: string;
  data: Record<string, unknown>;
  timestamp: number;
}

export function v2StreamAnalysis(
  runId: string,
  onEvent: (event: AgentSSEEvent) => void,
  onError?: (error: Event) => void
): EventSource {
  const url = `${API_V2_BASE}/analysis/${encodeURIComponent(runId)}/stream`;
  const token = localStorage.getItem("access_token");
  const eventSource = new EventSource(
    token ? `${url}?token=${encodeURIComponent(token)}` : url
  );
  eventSource.addEventListener("agent_update", (e) => {
    try {
      const parsed = JSON.parse((e as MessageEvent).data) as AgentSSEEvent;
      onEvent(parsed);
    } catch {
      /* ignore parse errors */
    }
  });
  if (onError) eventSource.onerror = onError;
  return eventSource;
}

// ── Portfolio ──

export interface PortfolioHolding {
  id: number;
  ticker: string;
  shares: number;
  avg_cost: number;
  last_agent_run_id?: string | null;
  last_agent_action?: string | null;
  notes?: string | null;
  created_at?: string | null;
}

export interface PortfolioSummary {
  total_value: number;
  total_cost: number;
  total_pnl: number;
  total_pnl_pct: number;
  holdings: (PortfolioHolding & {
    current_price: number;
    value: number;
    cost: number;
    pnl: number;
    pnl_pct: number;
  })[];
}

export async function v2PortfolioList(): Promise<{ holdings: PortfolioHolding[] }> {
  const res = await fetch(`${API_V2_BASE}/portfolio`, { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { holdings: PortfolioHolding[] };
}

export async function v2PortfolioAdd(body: {
  ticker: string;
  shares: number;
  avg_cost: number;
  notes?: string;
}): Promise<{ message: string; id: number }> {
  const res = await fetch(`${API_V2_BASE}/portfolio`, {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { message: string; id: number };
}

export async function v2PortfolioRemove(holdingId: number): Promise<void> {
  const res = await fetch(`${API_V2_BASE}/portfolio/${holdingId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || res.statusText);
  }
}

export async function v2PortfolioSummary(): Promise<PortfolioSummary> {
  const res = await fetch(`${API_V2_BASE}/portfolio/summary`, { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as PortfolioSummary;
}

// ── Backtesting ──

export interface BacktestResult {
  id: number;
  ticker: string;
  trade_date: string;
  predicted_action?: string | null;
  confidence?: number | null;
  actual_return_1d?: number | null;
  actual_return_5d?: number | null;
  actual_return_20d?: number | null;
  was_correct?: boolean | null;
  status: string;
  llm_provider?: string | null;
  created_at?: string | null;
}

export async function v2BacktestRun(body: {
  ticker: string;
  trade_date: string;
  llm_provider?: string;
  strategy_id?: number;
}): Promise<{ status: string; backtest_id: number }> {
  const res = await fetch(`${API_V2_BASE}/backtest`, {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { status: string; backtest_id: number };
}

export async function v2BacktestHistory(params?: {
  limit?: number;
}): Promise<{
  backtests: BacktestResult[];
  count: number;
  accuracy: number | null;
  correct: number;
  total_evaluated: number;
}> {
  const q = new URLSearchParams();
  if (params?.limit != null) q.set("limit", String(params.limit));
  const res = await fetch(`${API_V2_BASE}/backtest/history?${q}`, {
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as {
    backtests: BacktestResult[];
    count: number;
    accuracy: number | null;
    correct: number;
    total_evaluated: number;
  };
}

// ── Multi-Model Comparison ──

export async function v2Compare(body: {
  ticker: string;
  trade_date: string;
  providers?: string[];
  strategy_id?: number;
}): Promise<{ status: string; ticker: string; trade_date: string; runs: Record<string, string> }> {
  const res = await fetch(`${API_V2_BASE}/compare`, {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { status: string; ticker: string; trade_date: string; runs: Record<string, string> };
}

// ── Watchlists ──

export interface WatchlistItem {
  id: number;
  ticker: string;
  last_action?: string | null;
  last_run_id?: string | null;
}

export interface WatchlistData {
  id: number;
  name: string;
  schedule?: string | null;
  notify_on_change: boolean;
  is_active: boolean;
  last_run_at?: string | null;
  items: WatchlistItem[];
}

export async function v2WatchlistsList(): Promise<{ watchlists: WatchlistData[] }> {
  const res = await fetch(`${API_V2_BASE}/watchlists`, { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { watchlists: WatchlistData[] };
}

export async function v2WatchlistsCreate(body: {
  name: string;
  schedule?: string;
  tickers?: string[];
  notify_on_change?: boolean;
}): Promise<{ message: string; id: number }> {
  const res = await fetch(`${API_V2_BASE}/watchlists`, {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { message: string; id: number };
}

export async function v2WatchlistsUpdate(
  wlId: number,
  body: { name?: string; schedule?: string; notify_on_change?: boolean; is_active?: boolean }
): Promise<void> {
  const res = await fetch(`${API_V2_BASE}/watchlists/${wlId}`, {
    method: "PUT",
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || res.statusText);
  }
}

export async function v2WatchlistsDelete(wlId: number): Promise<void> {
  const res = await fetch(`${API_V2_BASE}/watchlists/${wlId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || res.statusText);
  }
}

export async function v2WatchlistsAddItem(
  wlId: number,
  ticker: string
): Promise<{ message: string; id: number }> {
  const res = await fetch(`${API_V2_BASE}/watchlists/${wlId}/items`, {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify({ ticker }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { message: string; id: number };
}

export async function v2WatchlistsRemoveItem(wlId: number, itemId: number): Promise<void> {
  const res = await fetch(`${API_V2_BASE}/watchlists/${wlId}/items/${itemId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || res.statusText);
  }
}

export async function v2WatchlistsRun(
  wlId: number,
  body?: { strategy_id?: number }
): Promise<{ status: string; runs: Record<string, string> }> {
  const res = await fetch(`${API_V2_BASE}/watchlists/${wlId}/run`, {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(body ?? {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { status: string; runs: Record<string, string> };
}

// ── Strategies ──

export interface StrategyData {
  id: number;
  name: string;
  description?: string | null;
  config: Record<string, unknown>;
  is_public: boolean;
  clone_count: number;
  backtest_accuracy?: number | null;
  author_id?: number;
  created_at?: string | null;
}

export async function v2StrategiesList(): Promise<{ strategies: StrategyData[] }> {
  const res = await fetch(`${API_V2_BASE}/strategies`, { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { strategies: StrategyData[] };
}

export async function v2StrategiesCreate(body: {
  name: string;
  description?: string;
  config?: Record<string, unknown>;
  is_public?: boolean;
}): Promise<{ message: string; id: number }> {
  const res = await fetch(`${API_V2_BASE}/strategies`, {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { message: string; id: number };
}

export async function v2StrategiesUpdate(
  stratId: number,
  body: { name?: string; description?: string; config?: Record<string, unknown>; is_public?: boolean }
): Promise<void> {
  const res = await fetch(`${API_V2_BASE}/strategies/${stratId}`, {
    method: "PUT",
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || res.statusText);
  }
}

export async function v2StrategiesDelete(stratId: number): Promise<void> {
  const res = await fetch(`${API_V2_BASE}/strategies/${stratId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || res.statusText);
  }
}

export async function v2StrategiesClone(
  stratId: number
): Promise<{ message: string; id: number }> {
  const res = await fetch(`${API_V2_BASE}/strategies/${stratId}/clone`, {
    method: "POST",
    headers: authHeaders(true),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { message: string; id: number };
}

export async function v2StrategiesMarketplace(params?: {
  sort?: "accuracy" | "clones" | "recent";
  limit?: number;
}): Promise<{ strategies: StrategyData[] }> {
  const q = new URLSearchParams();
  if (params?.sort) q.set("sort", params.sort);
  if (params?.limit != null) q.set("limit", String(params.limit));
  const res = await fetch(`${API_V2_BASE}/strategies/marketplace?${q}`, {
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { strategies: StrategyData[] };
}

// ── API Keys ──

export interface APIKeyData {
  id: number;
  name: string;
  key_prefix: string;
  is_active: boolean;
  rate_limit: number;
  total_requests: number;
  last_used_at?: string | null;
  expires_at?: string | null;
  created_at?: string | null;
}

export async function v2ApiKeysList(): Promise<{ api_keys: APIKeyData[] }> {
  const res = await fetch(`${API_V2_BASE}/api-keys`, { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { api_keys: APIKeyData[] };
}

export async function v2ApiKeysCreate(body: {
  name: string;
  rate_limit?: number;
  expires_days?: number;
}): Promise<{ message: string; id: number; api_key: string; warning: string }> {
  const res = await fetch(`${API_V2_BASE}/api-keys`, {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { message: string; id: number; api_key: string; warning: string };
}

export async function v2ApiKeysRevoke(keyId: number): Promise<void> {
  const res = await fetch(`${API_V2_BASE}/api-keys/${keyId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || res.statusText);
  }
}

// ── Team Collaboration ──

export interface TeamActivity {
  run_id: string;
  ticker: string;
  status: string;
  user_name?: string | null;
  user_email?: string | null;
  created_at?: string | null;
  llm_provider?: string | null;
}

export interface TeamMember {
  id: number;
  full_name: string;
  email: string;
  role?: string | null;
  created_at?: string | null;
}

export async function v2TeamActivity(
  params?: { limit?: number }
): Promise<{ activities: TeamActivity[]; count: number }> {
  const q = new URLSearchParams();
  if (params?.limit != null) q.set("limit", String(params.limit));
  const res = await fetch(`${API_V2_BASE}/team/activity?${q}`, { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { activities: TeamActivity[]; count: number };
}

export async function v2TeamSharedWatchlists(): Promise<{ watchlists: WatchlistData[] }> {
  const res = await fetch(`${API_V2_BASE}/team/shared-watchlists`, { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { watchlists: WatchlistData[] };
}

export async function v2TeamMembers(): Promise<{ members: TeamMember[] }> {
  const res = await fetch(`${API_V2_BASE}/team/members`, { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { members: TeamMember[] };
}

// ── Broker (Alpaca) ──

export async function v2TradeExecute(body: {
  run_id: string;
  quantity?: number;
}): Promise<Record<string, unknown>> {
  const res = await fetch(`${API_V2_BASE}/trade/execute`, {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as Record<string, unknown>;
}

export async function v2TradeOrders(): Promise<{ orders: Record<string, unknown>[] }> {
  const res = await fetch(`${API_V2_BASE}/trade/orders`, { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as { orders: Record<string, unknown>[] };
}
